import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const toFixed = (value, digits = 2) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(digits) : Number(0).toFixed(digits);
};

export function exportCashFlowReportToExcel({
  filename = "cash_flow_report.xlsx",
  filters = {},
  summary = [],
  accounts = [],
  daily = [],
  records = [],
}) {
  const rows = [];

  rows.push(["Cash Flow Report"]);
  rows.push([
    "Date Range",
    `${filters.start_date || "--"} to ${filters.end_date || "--"}`,
  ]);
  rows.push([]);

  rows.push(["Summary"]);
  rows.push([
    "Currency",
    "Opening Balance",
    "Inflow",
    "Outflow",
    "Net",
    "Closing Balance",
    "Invoice Payments",
    "Purchase Payments",
    "Transfer In",
    "Transfer Out",
    "Expenses",
    "Payroll",
    "Advance",
    "Other Inflow",
    "Other Outflow",
  ]);
  summary.forEach((row) => {
    rows.push([
      row.currency,
      toFixed(row.opening_balance),
      toFixed(row.inflow),
      toFixed(row.outflow),
      toFixed(row.net),
      toFixed(row.closing_balance),
      toFixed(row.invoice_payments),
      toFixed(row.purchase_payments),
      toFixed(row.transfer_in),
      toFixed(row.transfer_out),
      toFixed(row.expenses),
      toFixed(row.payroll),
      toFixed(row.advance),
      toFixed(row.other_inflow),
      toFixed(row.other_outflow),
    ]);
  });

  rows.push([]);
  rows.push(["Account Summary"]);
  rows.push([
    "Account",
    "Branch",
    "Currency",
    "Opening",
    "Inflow",
    "Outflow",
    "Transfer In",
    "Transfer Out",
    "Net",
    "Closing",
  ]);
  accounts.forEach((row) => {
    rows.push([
      row.account_name,
      row.shop_name,
      row.currency,
      toFixed(row.opening_balance),
      toFixed(row.inflow),
      toFixed(row.outflow),
      toFixed(row.transfer_in),
      toFixed(row.transfer_out),
      toFixed(row.net),
      toFixed(row.closing_balance),
    ]);
  });

  rows.push([]);
  rows.push(["Daily Cash Flow"]);
  rows.push(["Date", "Currency", "Inflow", "Outflow", "Transfer In", "Transfer Out", "Net"]);
  daily.forEach((row) => {
    rows.push([
      row.date,
      row.currency,
      toFixed(row.inflow),
      toFixed(row.outflow),
      toFixed(row.transfer_in),
      toFixed(row.transfer_out),
      toFixed(row.net),
    ]);
  });

  rows.push([]);
  rows.push(["Full Records"]);
  rows.push([
    "Date",
    "Branch",
    "Account",
    "Category",
    "Type",
    "Transaction Currency",
    "Amount",
    "Account Currency",
    "Converted Amount",
    "Description",
  ]);
  records.forEach((row) => {
    rows.push([
      row.transaction_date,
      row.shop_name,
      row.account_name,
      row.category,
      row.transaction_type,
      row.transaction_currency,
      toFixed(row.amount),
      row.account_currency,
      toFixed(row.converted_amount),
      row.description || "",
    ]);
  });

  const worksheet = XLSX.utils.aoa_to_sheet(rows);
  worksheet["!cols"] = [
    { wch: 14 },
    { wch: 18 },
    { wch: 22 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 14 },
    { wch: 16 },
    { wch: 48 },
  ];
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Cash Flow");
  XLSX.writeFile(workbook, filename);
}

export function exportCashFlowReportToPDF({
  filename = "cash_flow_report.pdf",
  filters = {},
  summary = [],
  accounts = [],
  daily = [],
  records = [],
}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const generatedAt = new Date().toLocaleString();

  const drawHeader = (title) => {
    doc.setFontSize(15);
    doc.setTextColor(33, 33, 33);
    doc.text(title, pageWidth / 2, 10, { align: "center" });
    doc.setFontSize(9);
    doc.setTextColor(90, 90, 90);
    doc.text(`Generated: ${generatedAt}`, 10, 16);
    doc.text(
      `Date Range: ${filters.start_date || "--"} to ${filters.end_date || "--"}`,
      pageWidth - 10,
      16,
      { align: "right" }
    );
    doc.setDrawColor(220, 220, 220);
    doc.line(10, 19, pageWidth - 10, 19);
  };

  const drawFooter = () => {
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      doc.setDrawColor(220, 220, 220);
      doc.line(10, pageHeight - 10, pageWidth - 10, pageHeight - 10);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - 10, pageHeight - 5, {
        align: "right",
      });
    }
  };

  drawHeader("Cash Flow Report");

  autoTable(doc, {
    startY: 24,
    head: [[
      "Currency",
      "Opening",
      "Inflow",
      "Outflow",
      "Net",
      "Closing",
      "Invoice",
      "Purchase",
      "Tr In",
      "Tr Out",
      "Expenses",
      "Payroll",
      "Advance",
    ]],
    body: summary.map((row) => [
      row.currency,
      toFixed(row.opening_balance),
      toFixed(row.inflow),
      toFixed(row.outflow),
      toFixed(row.net),
      toFixed(row.closing_balance),
      toFixed(row.invoice_payments),
      toFixed(row.purchase_payments),
      toFixed(row.transfer_in),
      toFixed(row.transfer_out),
      toFixed(row.expenses),
      toFixed(row.payroll),
      toFixed(row.advance),
    ]),
    headStyles: { fillColor: [41, 98, 255], textColor: [255, 255, 255] },
    styles: { fontSize: 7, cellPadding: 2 },
    margin: { left: 10, right: 10 },
  });

  let y = doc.lastAutoTable.finalY + 6;
  if (y > pageHeight - 24) {
    doc.addPage();
    drawHeader("Cash Flow Report");
    y = 24;
  }

  doc.setFontSize(11);
  doc.setTextColor(33, 33, 33);
  doc.text("Account Summary", 10, y);
  autoTable(doc, {
    startY: y + 2,
    head: [["Account", "Branch", "Currency", "Opening", "Inflow", "Outflow", "Net", "Closing"]],
    body: accounts.map((row) => [
      row.account_name,
      row.shop_name,
      row.currency,
      toFixed(row.opening_balance),
      toFixed(row.inflow),
      toFixed(row.outflow),
      toFixed(row.net),
      toFixed(row.closing_balance),
    ]),
    headStyles: { fillColor: [0, 121, 107], textColor: [255, 255, 255] },
    styles: { fontSize: 7, cellPadding: 2 },
    margin: { left: 10, right: 10 },
  });

  y = doc.lastAutoTable.finalY + 6;
  if (y > pageHeight - 24) {
    doc.addPage();
    drawHeader("Cash Flow Report");
    y = 24;
  }

  doc.setFontSize(11);
  doc.text("Daily Cash Flow", 10, y);
  autoTable(doc, {
    startY: y + 2,
    head: [["Date", "Currency", "Inflow", "Outflow", "Tr In", "Tr Out", "Net"]],
    body: daily.map((row) => [
      row.date,
      row.currency,
      toFixed(row.inflow),
      toFixed(row.outflow),
      toFixed(row.transfer_in),
      toFixed(row.transfer_out),
      toFixed(row.net),
    ]),
    headStyles: { fillColor: [123, 31, 162], textColor: [255, 255, 255] },
    styles: { fontSize: 7, cellPadding: 2 },
    margin: { left: 10, right: 10 },
  });

  y = doc.lastAutoTable.finalY + 6;
  if (y > pageHeight - 40) {
    doc.addPage();
    drawHeader("Cash Flow Report");
    y = 24;
  }

  doc.setFontSize(11);
  doc.text("Full Records", 10, y);
  autoTable(doc, {
    startY: y + 2,
    head: [[
      "Date",
      "Branch",
      "Account",
      "Category",
      "Type",
      "Txn Cur",
      "Amount",
      "Acc Cur",
      "Converted",
      "Description",
    ]],
    body: records.map((row) => [
      row.transaction_date,
      row.shop_name,
      row.account_name,
      row.category,
      row.transaction_type,
      row.transaction_currency,
      toFixed(row.amount),
      row.account_currency,
      toFixed(row.converted_amount),
      row.description || "",
    ]),
    headStyles: { fillColor: [194, 65, 12], textColor: [255, 255, 255] },
    styles: { fontSize: 6.5, cellPadding: 1.8 },
    columnStyles: {
      0: { cellWidth: 18 },
      1: { cellWidth: 24 },
      2: { cellWidth: 28 },
      3: { cellWidth: 22 },
      4: { cellWidth: 15 },
      5: { cellWidth: 14 },
      6: { cellWidth: 18, halign: "right" },
      7: { cellWidth: 14 },
      8: { cellWidth: 18, halign: "right" },
      9: { cellWidth: 85 },
    },
    margin: { left: 10, right: 10 },
  });

  drawFooter();
  doc.save(filename);
}
